import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import * as IPurchases from '../../../../../interfaces/merchants/products/purchases';
import * as ICustomFields from '../../../../../interfaces/merchants/products/purchases/custom_fields';
import PurchaseCheckInput, {
  InputType,
} from '../common/forms/PurchaseCheckInput';
import PurchaseSelect from '../common/forms/PurchaseSelect';
import PurchaseTextInput, {
  TextInputType,
} from '../common/forms/PurchaseTextInput';
import PurchaseLabeledContainer from '../common/PurchaseLabeledContainer';
import ErrorMessage from './ErrorMessage';

interface Props {
  customerFields: IPurchases.CustomerField[];
  onChange: (name: string, value: string) => void;
  namePrefix: 'customerFields';
  values: { [name: string]: string };
}

const textValueCorrection = (
  field: IPurchases.CustomerField,
  value: string,
) => {
  switch (field.validationType) {
    case ICustomFields.ValidationType.Alphanumeric:
      return value.replace(/[^0-9a-zA-Zａ-ｚＡ-Ｚ０-９]+/i, '');
    case ICustomFields.ValidationType.Numeric:
      return value.replace(/[^0-9０-９]+/i, '');
    case ICustomFields.ValidationType.Phone:
      return value
        .replace(/[０-９]/g, s => {
          return String.fromCharCode(s.charCodeAt(0) - 65248);
        })
        .replace(/[‐－―ー]/g, '-')
        .replace(/[^\d\-]/g, '')
        .substr(0, 20);
    case ICustomFields.ValidationType.Email:
      return value
        .replace(/[！-～]/g, s => {
          return String.fromCharCode(s.charCodeAt(0) - 65248);
        })
        .substr(0, 100);
    default:
      return value;
  }
};

const inputValidation = (field: IPurchases.CustomerField, value: string) => {
  if (field.inputType === ICustomFields.InputType.TextField) {
    return textValueCorrection(field, value).substr(0, 1000);
  } else {
    return value.substr(0, 1000);
  }
};

export const submittable = (
  customerFields: IPurchases.CustomerField[],
  values: { [id: number]: string },
): boolean => {
  return customerFields.every(
    f => !f.required || (f.required && !!values[f.id]),
  );
};

export const createInitialState = (
  customerFields?: IPurchases.CustomerField[],
) => {
  if (!customerFields) {
    return {};
  }
  return customerFields.reduce((acc, field) => {
    acc[field.id] = field.defaultValue || '';
    return acc;
  }, {});
};

const toLabel = (
  field: IPurchases.CustomerField,
  intl: ReactIntl.InjectedIntl,
) => {
  if (field.inputType !== ICustomFields.InputType.TextField) {
    return field.label;
  }
  switch (field.validationType) {
    case ICustomFields.ValidationType.Alphanumeric:
      return `${field.label} (${intl.formatMessage({
        id: `purchases.validationLabel.alphanumeric`,
      })})`;
    case ICustomFields.ValidationType.Numeric:
      return `${field.label} (${intl.formatMessage({
        id: `purchases.validationLabel.numeric`,
      })})`;
    case ICustomFields.ValidationType.Katakana:
      return `${field.label} (${intl.formatMessage({
        id: `purchases.validationLabel.katakana`,
      })})`;
    default:
      return field.label;
  }
};

class CustomerFields extends React.Component<Props & InjectedIntlProps> {
  constructor(props: Props & InjectedIntlProps) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
    this.renderInput = this.renderInput.bind(this);
  }

  private handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    field: IPurchases.CustomerField,
  ) {
    const {
      currentTarget: { value },
    } = e;
    const { namePrefix } = this.props;
    const applyValue = inputValidation(field, value);
    this.props.onChange(`${namePrefix}.${field.id}`, applyValue);
  }

  private renderInput(field: IPurchases.CustomerField) {
    const { values } = this.props;
    switch (field.inputType) {
      case ICustomFields.InputType.TextField:
        return (
          <PurchaseTextInput
            type={TextInputType.Text}
            name={field.label}
            value={values[field.id]}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              this.handleChange(e, field)
            }
          />
        );
      case ICustomFields.InputType.TextArea:
        return (
          <PurchaseTextInput
            type={TextInputType.TextArea}
            rows={6}
            name={field.label}
            value={values[field.id]}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              this.handleChange(e, field)
            }
          />
        );
      case ICustomFields.InputType.SelectBox:
        return (
          <PurchaseSelect
            name={field.label}
            value={values[field.id] || ''}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              this.handleChange(e, field)
            }
            selected={values[field.id] !== ''}
          >
            {field.valueOptions!.map((o, i) => (
              <option key={i} value={o}>
                {o}
              </option>
            ))}
          </PurchaseSelect>
        );
      case ICustomFields.InputType.RadioButton:
        return field.valueOptions!.map((o, i) => (
          <PurchaseCheckInput
            key={i}
            type={InputType.Radio}
            value={o}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              this.handleChange(e, field)
            }
            checked={((values[field.id] as string) || '') === o}
          >
            {o}
          </PurchaseCheckInput>
        ));
      default:
        return (
          <PurchaseTextInput
            type={TextInputType.Text}
            name={field.label}
            value={values[field.id]}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              this.handleChange(e, field)
            }
          />
        );
    }
  }

  public render() {
    const { intl, customerFields } = this.props;

    return customerFields.map(f => (
      <div key={`${f.id}-${f.label}`}>
        <PurchaseLabeledContainer
          label={toLabel(f, intl)}
          requiredLabel={f.required}
          errorStyle={f.errors && f.errors.length !== 0}
          paddingTop={true}
          description={f.description}
        >
          {this.renderInput(f)}
        </PurchaseLabeledContainer>
        <ErrorMessage errors={f.errors} />
      </div>
    ));
  }
}

export default injectIntl(CustomerFields);
